import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type SettingsOut = components['schemas']['SettingsOut']
export type SettingsIn = components['schemas']['SettingsIn']
export type OllamaStatus = components['schemas']['OllamaStatusOut']
export type PhoneCheck = components['schemas']['PhoneCheckOut']
export type PhoneCheckStep = components['schemas']['PhoneCheckStepOut']

/**
 * "Testar celular": confere a conexão com o celular da capa dos Shorts, passo
 * a passo (ADB, autorização, agente, se ele obedece, app do YouTube). Só o
 * admin; 429 dentro de 30 s. Mutation, e não query: acende a tela do aparelho.
 */
export function usePhoneCheck() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/settings/phone-check')
      if (!data) throw new Error('phone check empty')
      return data.data
    },
  })
}

export type StudioCoversSwitch = components['schemas']['StudioCoversSwitchOut']

/** O interruptor "Automação de capas" do Prism Device. Só o admin; 403 aos outros. */
export function useStudioCoversSwitch() {
  return useQuery({
    queryKey: queryKeys.settings.studioCovers(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/settings/studio-covers')
      if (!data) throw new Error('studio covers switch empty')
      return data.data
    },
  })
}

/**
 * Liga ou desliga a automação de capas. Desligada, nenhuma drenagem roda e a
 * faixa "Capas dos Shorts" do Scheduler some; por isso invalida também o
 * status de capas do Scheduler.
 */
export function useSetStudioCoversSwitch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data } = await api.PUT('/api/v1/settings/studio-covers', { body: { enabled } })
      if (!data) throw new Error('studio covers switch update empty')
      return data.data
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.settings.studioCovers(), data)
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.studioThumbnails() })
    },
  })
}

export type PhoneLock = components['schemas']['PhoneLockOut']
export type PhoneUnlock = components['schemas']['PhoneUnlockOut']

/** O PIN da tela de bloqueio do celular: se existe e o estado. A API nunca devolve o PIN. Só o admin. */
export function usePhoneLock() {
  return useQuery({
    queryKey: queryKeys.settings.phoneLock(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/settings/phone-lock')
      if (!data) throw new Error('phone lock empty')
      return data.data
    },
  })
}

/**
 * Grava o PIN, digitado duas vezes. ``gcTime: 0``: a mutation guarda as
 * variáveis (o PIN) no cache dela, e assim elas somem assim que o painel
 * solta a mutation. O painel ainda chama ``reset()`` depois do sucesso.
 */
export function useSavePhoneLock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { pin: string; pin_confirm: string }) => {
      const { data } = await api.PUT('/api/v1/settings/phone-lock', { body: input })
      if (!data) throw new Error('phone lock update empty')
      return data.data
    },
    gcTime: 0,
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.settings.phoneLock(), data)
    },
  })
}

export function useDeletePhoneLock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.DELETE('/api/v1/settings/phone-lock')
      if (!data) throw new Error('phone lock delete empty')
      return data.data
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.settings.phoneLock(), data)
    },
  })
}

/**
 * "Desbloquear agora": confere o PIN salvo no aparelho, com o dono olhando, e
 * arma a automação se destravou. Mutation: mexe na tela. 429 dentro de 30 s.
 */
export function usePhoneUnlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/settings/phone-unlock')
      if (!data) throw new Error('phone unlock empty')
      return data.data
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.settings.phoneLock(), data.lock)
    },
  })
}

/**
 * Singleton ``global_settings`` row. Each field is the explicit override
 * or ``null`` (meaning: "fall back to env / hardcoded default"). The form
 * shows env-derived placeholders for unset fields so the user knows what
 * value is currently in effect.
 */
export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings.global(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/settings')
      if (!data) throw new Error('settings empty')
      return data.data
    },
    staleTime: 30_000,
  })
}

/**
 * On-demand reachability probe of the configured desktop Ollama host.
 * Disabled by default (``enabled: false``) — the probe hits the network, so
 * we only run it when the user clicks "Testar" (call ``refetch()``). Mirrors
 * the CDP status shape: always 200, branch on ``available``.
 */
export function useOllamaStatus() {
  return useQuery({
    queryKey: queryKeys.settings.ollamaStatus(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/settings/ollama-status')
      if (!data) throw new Error('ollama status empty')
      return data.data
    },
    enabled: false,
    gcTime: 0,
  })
}

/** Partial update — pass only fields the user actually changed. */
export function useUpdateSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SettingsIn) => {
      const { data } = await api.PUT('/api/v1/settings', { body: input })
      if (!data) throw new Error('settings update empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settings.all })
    },
  })
}
